import React, { useEffect, useState } from "react";
import { API, addOrUpdateFooter } from "../../api/api";
import "./FooterAdminPanel.css";

const FOOTER_SECTION_KEYS = ["quick", "another", "more", "additional"];
const DEFAULT_FOOTER_TITLES = {
  quick: "Quick Links",
  another: "Popular Links",
  more: "Resources",
  additional: "Support",
};

const normalizeFooterData = (data = {}) =>
  FOOTER_SECTION_KEYS.reduce(
    (accumulator, key) => {
      accumulator[key] = Array.isArray(data?.[key]) ? data[key] : [];
      return accumulator;
    },
    {
      titles: FOOTER_SECTION_KEYS.reduce((titleAccumulator, key) => {
        titleAccumulator[key] = String(data?.titles?.[key] || DEFAULT_FOOTER_TITLES[key]);
        return titleAccumulator;
      }, {}),
    }
  );

function FooterAdminPanel() {
  const [section, setSection] = useState("quick");
  const [formData, setFormData] = useState({ title: "", link: "" });
  const [sectionTitles, setSectionTitles] = useState(DEFAULT_FOOTER_TITLES);

  const [footerData, setFooterData] = useState({
    titles: DEFAULT_FOOTER_TITLES,
    quick: [],
    another: [],
    more: [],
    additional: [],
  });
  const [loading, setLoading] = useState(false);

  

  const fetchFooterData = async () => {
    try {
      setLoading(true);

      let res;
      try {
        res = await API.get("/footer");
      } catch {
        // fallback if backend route differs
        res = await API.get("/footer/get");
      }

      const normalized = normalizeFooterData(res?.data || {});
      setFooterData(normalized);
      setSectionTitles(normalized.titles || DEFAULT_FOOTER_TITLES);
    } catch (err) {
      console.error("Error fetching footer data:", err);
      setFooterData(normalizeFooterData({}));
      setSectionTitles(DEFAULT_FOOTER_TITLES);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterData();
  }, []);

  const role = localStorage.getItem("role");
  if (role !== "superadmin") {
    return (
      <div style={{ color: "red", textAlign: "center", marginTop: "2rem" }}>
        Access denied: Superadmin only
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.link.trim()) {
      alert("❌ Please fill in all fields");
      return;
    }

    try {
      const updated = normalizeFooterData(footerData);
      updated[section] = [...updated[section], formData];

      const res = await addOrUpdateFooter(updated);
      alert(res?.data?.message || "✅ Footer link added successfully!");

      setFormData({ title: "", link: "" });
      await fetchFooterData();
    } catch (err) {
      console.error("Error adding footer link:", err);
      alert("❌ Error: Could not add link, please try again.");
    }
  };

  const handleTitleChange = (sectionKey, value) => {
    setSectionTitles((current) => ({ ...current, [sectionKey]: value }));
  };

  const handleSaveTitles = async () => {
    const trimmedTitles = FOOTER_SECTION_KEYS.reduce((accumulator, key) => {
      accumulator[key] = String(sectionTitles[key] || "").trim() || DEFAULT_FOOTER_TITLES[key];
      return accumulator;
    }, {});

    try {
      const updated = { ...normalizeFooterData(footerData), titles: trimmedTitles };
      const res = await addOrUpdateFooter(updated);
      alert(res?.data?.message || "✅ Footer titles updated successfully!");

      setSectionTitles(trimmedTitles);
      await fetchFooterData();
    } catch (err) {
      console.error("Error saving footer titles:", err);
      alert("❌ Error: Could not update footer titles, please try again.");
    }
  };

  const handleDeleteLink = async (sectionKey, index) => {
    if (!window.confirm("Are you sure you want to delete this link?")) return;

    try {
      const updated = normalizeFooterData(footerData);
      updated[sectionKey] = updated[sectionKey].filter((_, i) => i !== index);

      const res = await addOrUpdateFooter(updated);
      alert(res?.data?.message || "✅ Link deleted successfully!");

      await fetchFooterData();
    } catch (err) {
      console.error("Error deleting footer link:", err);
      alert("❌ Error: Could not delete link, please try again.");
    }
  };

  const getSectionLabel = (sec) => sectionTitles[sec] || DEFAULT_FOOTER_TITLES[sec] || sec;

  return (
    <div className="footer-admin-container">
      <h2>🔧 Footer Management Panel</h2>

      <form onSubmit={handleSubmit} className="footer-form">
        <select value={section} onChange={(e) => setSection(e.target.value)}>
          {FOOTER_SECTION_KEYS.map((sec) => (
            <option key={sec} value={sec}>
              {getSectionLabel(sec)}
            </option>
          ))}
        </select>
        <input
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Link title"
        />
        <input
          name="link"
          value={formData.link}
          onChange={handleChange}
          placeholder="https://example.com"
        />
        <button type="submit">Add Link</button>
      </form>

      <h3>📝 Section Titles</h3>
      <div className="footer-title-editor">
        {FOOTER_SECTION_KEYS.map((sec) => (
          <div key={sec} style={{ marginBottom: 8 }}>
            <label style={{ display: "block", fontWeight: 600 }}>{sec}</label>
            <input
              value={sectionTitles[sec] || ""}
              onChange={(e) => handleTitleChange(sec, e.target.value)}
            />
          </div>
        ))}
        <button onClick={handleSaveTitles}>Save Titles</button>
      </div>

      <h3>📋 Existing Footer Links</h3>
      <div className="footer-sections">
        {loading ? (
          <div>Loading...</div>
        ) : (
          FOOTER_SECTION_KEYS.map((sec) => (
            <div key={sec} className="footer-column">
              <h4>{getSectionLabel(sec)}</h4>
              <ul>
                {Array.isArray(footerData[sec]) && footerData[sec].length > 0 ? (
                  footerData[sec].map((item, i) => (
                    <li key={i}>
                      <a href={item.link} target="_blank" rel="noreferrer" title={item.link}>
                        {item.title}
                      </a>
                      <button
                        className="footer-delete-button"
                        onClick={() => handleDeleteLink(sec, i)}
                      >
                        🗑️ Delete
                      </button>
                    </li>
                  ))
                ) : (
                  <li style={{ color: "#a0aec0", fontStyle: "italic" }}>No links added yet</li>
                )}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default FooterAdminPanel;